

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Loading from "../Common/Loading";

interface FileInfo {
  name: string;      // display name (VB: hidDocNames entry)
  blobName: string;  // storage blob file name (VB: hidDocFiles entry)
}

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const AttachFile: React.FC = () => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const schoolCode = sessionStorage.getItem('SelectedSchoolCode') || '';
  const login = sessionStorage.getItem('L') || '';
  const fileName = searchParams.get('fileName') || '';

  const [files, setFiles] = useState<FileInfo[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<number[]>([]);
  const [docName, setDocName] = useState('');
  const [uploadId, setUploadId] = useState('');
  const [filePrefix, setFilePrefix] = useState('');
  const [blobPath, setBlobPath] = useState('');
  const [dispType, setDispType] = useState<'block' | 'none'>('block');
  const [noOfAttachments, setNoOfAttachments] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(true);

  // hidDocNames / hidDocFiles / hidFamCodeDocID, held client-side.
  // In the VB page these were hidden <input> fields that ASP.NET
  // ViewState resubmitted on every postback automatically. There's no
  // ViewState here, so this component holds the same three strings in
  // state and sends them back on every upload/delete call itself --
  // that's the exact equivalent, not a new persistence mechanism.
  const [hidDocNames, setHidDocNames] = useState('');
  const [hidDocFiles, setHidDocFiles] = useState('');
  const [hidFamCodeDocID, setHidFamCodeDocID] = useState('');

  const fileUploadRef = useRef<HTMLInputElement>(null);

  // --- Initial load: mirrors VB Page_Load (IsPostBack = False) ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}api/attach-file/?schoolCode=${encodeURIComponent(schoolCode)}` +
        `&login=${encodeURIComponent(login)}&fileName=${encodeURIComponent(fileName)}`
      );

      if (!response.ok) throw new Error('Failed to load data');

      const data = await response.json();
      if (data.success) {
        // VB never loads an existing attachment list on initial load --
        // files is always empty and dispType is always "block" here.
        setFiles([]);
        setUploadId(data.uploadId);
        setFilePrefix(data.filePrefix);
        setBlobPath(data.blobPath);
        setDispType(data.dispType);
        setNoOfAttachments(data.noOfAttachments);
      } else {
        setErrorMsg(data.error || 'Failed to load data');
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [schoolCode, login, fileName]);

  useEffect(() => {
    if (schoolCode && login) {
      fetchData();
    }
  }, [fetchData]);

  useEffect(() => {
    if (errorMsg) {
      alert(errorMsg);
      setErrorMsg('');
    }
  }, [errorMsg]);

  // --- Validate: mirrors VB fn_Validate() ---
  const fn_Validate = (): boolean => {
    if (!docName.trim()) {
      alert("Enter the name.");
      return false;
    }
    if (docName.includes('|') || docName.includes('*')) {
      alert('Document name cannot contain | or *');
      return false;
    }
    if (!fileUploadRef.current?.files?.length) {
      return false;
    }
    return true;
  };

  // --- Upload: mirrors VB cmdUpload_Click ---
  const handleUpload = async () => {
    if (!fn_Validate()) return;

    setLoading(true);
    try {
      const file = fileUploadRef.current?.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('schoolCode', schoolCode);
      formData.append('login', login);
      formData.append('docName', docName);
      formData.append('uploadId', uploadId);
      formData.append('filePrefix', filePrefix);
      // Resubmit current hidden-field values, same as a VB postback would.
      formData.append('currentDocNames', hidDocNames);
      formData.append('currentDocFiles', hidDocFiles);
      formData.append('currentFamCodeDocID', hidFamCodeDocID);

      const response = await fetch(`${API_BASE_URL}api/attach-file/upload/`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to upload file');

      const data = await response.json();
      if (data.success) {
        const newFile: FileInfo = {
          name: docName,
          blobName: data.blobName,
        };

        setFiles(prev => [...prev, newFile]);
        setHidDocNames(data.docNames);
        setHidDocFiles(data.docFiles);
        setHidFamCodeDocID(data.famCodeDocID);
        setDocName('');
        setNoOfAttachments(prev => prev + 1);

        if (fileUploadRef.current) {
          fileUploadRef.current.disabled = true;
          fileUploadRef.current.value = '';
        }
      } else {
        setErrorMsg(data.error || 'Failed to upload file');
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const picked = e.target.files[0].name;
      if (!docName) {
        setDocName(picked.split('.').slice(0, -1).join('.').slice(0, 10));
      }
    }
  };

  // --- Delete: mirrors VB fn_Delete() / imgDelete_Click ---
  const fn_Delete = (): boolean => {
    if (selectedFiles.length === 0) {
      alert('Please select attachment(s) to delete.');
      return false;
    }
    if (!window.confirm("Do you really want to delete the selected attachment(s)?")) {
      return false;
    }
    deleteFiles();
    return true;
  };

  const deleteFiles = async () => {
    if (selectedFiles.length === 0) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('schoolCode', schoolCode);
      formData.append('login', login);
      formData.append('deleteIndices', selectedFiles.join('|'));
      formData.append('uploadId', uploadId);
      // Resubmit current hidden-field values, same as a VB postback would.
      formData.append('currentDocNames', hidDocNames);
      formData.append('currentDocFiles', hidDocFiles);
      formData.append('currentFamCodeDocID', hidFamCodeDocID);

      const response = await fetch(`${API_BASE_URL}api/attach-file/delete/`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to delete files');

      const data = await response.json();
      if (data.success) {
        const newFiles = files.filter((_, index) => !selectedFiles.includes(index));
        setFiles(newFiles);
        setHidDocNames(data.docNames);
        setHidDocFiles(data.docFiles);
        setHidFamCodeDocID(data.famCodeDocID);
        setSelectedFiles([]);
        setNoOfAttachments(newFiles.length);

        if (newFiles.length === 0 && fileUploadRef.current) {
          fileUploadRef.current.disabled = false;
        }
      } else {
        setErrorMsg(data.error || 'Failed to delete files');
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (index: number) => {
    setSelectedFiles(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  // --- Close: mirrors VB fn_CloseWindowAndReturn() ---
  // VB: if hidDocNames.Value <> "" AndAlso hidDocFiles.Value <> "" then
  // build ret from those two hidden fields + hidUploadID.Value.
  const fn_CloseWindowAndReturn = () => {
    if (hidDocNames !== '' && hidDocFiles !== '') {
      const ret = `${hidDocNames}*${hidDocFiles}*${uploadId}`;

      if (window.opener) {
        (window.opener as any).ret = ret;
        window.opener.location.reload();
      }
    }
    window.close();
    return false;
  };

  const docListHeight = dispType === 'none' ? '220px' : '150px';

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px' }}><Loading /></div>;
  }

  return (
    <div className="cssbody" style={{ margin: '5px', fontFamily: 'Trebuchet MS', fontSize: '10pt' }}>
      <div style={{
        border: 'solid 1px #000080',
        backgroundColor: '#f8f8f8',
        margin: '10px',
        textAlign: 'center',
      }}>
        <table style={{ width: '97%', display: dispType }} align="center">
          <tbody>
            <tr>
              <td align="left" style={{ width: '15%' }}>Name:</td>
              <td align="left" style={{ width: '85%' }}>
                <input
                  type="text"
                  id="txtDocName"
                  value={docName}
                  onChange={(e) => setDocName(e.target.value)}
                  style={{ width: '320px', backgroundColor: 'white', borderStyle: 'ridge' }}
                  maxLength={10}
                />
                <br />
              </td>
            </tr>

            <tr>
              <td align="left">Location:</td>
              <td align="left">
                <input
                  type="file"
                  id="FileUpload1"
                  ref={fileUploadRef}
                  onChange={handleFileChange}
                  style={{ width: '325px', backgroundColor: 'white', borderStyle: 'ridge' }}
                  disabled={dispType === 'none'}
                />
              </td>
            </tr>

            <tr style={{ height: '4px' }}><td></td></tr>

            <tr>
              <td colSpan={2} align="center" style={{ width: '100%' }}>
                <button
                  id="ImgAdd"
                  onClick={handleUpload}
                  style={{ width: '60px', marginRight: '10px' }}
                  disabled={dispType === 'none'}
                >
                  Save
                </button>
                <button
                  id="imgDelete"
                  onClick={() => fn_Delete()}
                  style={{ width: '60px', display: noOfAttachments > 0 ? 'inline-block' : 'none' }}
                >
                  Delete
                </button>
              </td>
            </tr>

            <tr style={{ height: '4px' }}><td></td></tr>
          </tbody>
        </table>

        {/* Documents list -- mirrors VB's fn_SetDocTable()-generated table */}
        <div
          id="divDocList"
          style={{
            width: '95%',
            height: docListHeight,
            overflowY: 'scroll',
            border: 'solid 1px #b0b0b0',
            backgroundColor: 'white',
            padding: '5px',
            textAlign: 'center',
          }}
        >
          <table width="95%" cellPadding={2} cellSpacing={0}>
            <tbody>
              <tr style={{ backgroundColor: '#e0e0e0', color: 'black' }}>
                <td align="left" style={{ paddingLeft: '5px', width: '95%' }}>Document</td>
                <td align="center" width="5%">&nbsp;</td>
              </tr>
              {files.length > 0 ? (
                files.map((file, index) => (
                  <tr key={index}>
                    <td style={{ paddingLeft: '5px', borderBottom: 'solid 1px #e2e2e2', textAlign: 'left' }}>
                      <a
                        href={`${blobPath}${file.blobName}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {file.name}
                      </a>
                    </td>
                    <td align="center" style={{ borderBottom: 'solid 1px #e2e2e2' }}>
                      <input
                        type="checkbox"
                        id={`chk${index}`}
                        checked={selectedFiles.includes(index)}
                        onChange={() => handleFileSelect(index)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2} style={{ padding: '10px', textAlign: 'center' }}>
                    No attachments
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <table width="100%">
        <tbody>
          <tr>
            <td style={{ width: '100%', paddingRight: '8px', height: '40px' }} align="right" valign="middle">
              <button id="imgClose" onClick={fn_CloseWindowAndReturn} style={{ width: '60px' }}>
                Close
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default AttachFile;